import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { phoneError } from "@/lib/phone";

/**
 * Renders a required (non-dismissible) modal after login if the user has no phone_number.
 * Admin uses this to reach the user about payments.
 */
export default function PhoneCollector() {
  const { user, refresh } = useAuth();
  const [phone, setPhone] = useState("+");
  const [saving, setSaving] = useState(false);

  const needsPhone = user && !user.banned && !user.phone_number;
  const err = phone === "+" ? "Enter your international number starting with +" : phoneError(phone);

  const save = async () => {
    const cleaned = phone.trim();
    const e = phoneError(cleaned);
    if (e) { toast.error(e); return; }
    setSaving(true);
    try {
      await api.post("/auth/phone", { phone_number: cleaned });
      toast.success("Phone number saved");
      await refresh();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!needsPhone}>
      <DialogContent
        data-testid="phone-collector"
        className="bg-[#121212] border-white/10 text-white max-w-sm"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-blue-500/15 flex items-center justify-center mx-auto mb-2">
            <Phone size={28} weight="fill" className="text-blue-400" />
          </div>
          <DialogTitle className="font-heading text-2xl text-center">
            Add your phone number
          </DialogTitle>
          <DialogDescription className="text-sm text-white/60 text-center mt-1">
            Admin will use this to reach you about payments and withdrawals. It stays private otherwise.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <Input
            data-testid="phone-input"
            type="tel"
            value={phone}
            onChange={(e) => {
              let v = e.target.value;
              if (!v.startsWith("+")) v = "+" + v.replace(/^\++/, "");
              setPhone(v);
            }}
            placeholder="+233 XX XXX XXXX"
            className="bg-white/5 border-white/10 text-white h-12 text-center text-lg"
            autoFocus
          />
          <p data-testid="phone-input-hint" className={`text-[11px] text-center ${err ? "text-red-400" : "text-emerald-400"}`}>
            {err || "Looks good"}
          </p>
          <Button
            data-testid="phone-save-btn"
            onClick={save}
            disabled={saving || !!err}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-heading font-bold"
          >
            Continue
          </Button>
          <p className="text-[10px] text-white/40 text-center flex items-center justify-center gap-1">
            <ShieldCheck size={12} weight="fill" /> Only admin sees your number
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
